import { useEffect, useMemo, useRef, useState } from "react";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { fetchDokumentPreviewPdf, type DokumentDetail } from "@/lib/dokumente";
import type { EditorPosition } from "@/pages/dokumente/documentPositionModel";
import {
  editorDocToPreviewPayload,
  editorPositionsToPreviewPayload,
} from "@/pages/dokumente/documentPreviewPayload";
import { DokumentPrintAction } from "@/pages/dokumente/DokumentPrintAction";

interface DocumentPreviewDialogProps {
  open: boolean;
  doc: DokumentDetail;
  positions: EditorPosition[];
  onClose: () => void;
  zIndexClass?: string;
  /** Entwurf: Erstellen / Erstellen und Versenden in der Vorschau anzeigen. */
  showCreateActions?: boolean;
  createDisabled?: boolean;
  createDisabledReason?: string;
  creating?: boolean;
  onCreate?: () => void | Promise<boolean | void>;
  onCreateAndSend?: () => void | Promise<boolean | void>;
}

function asPdfBlob(blob: Blob): Blob {
  return blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
}

export function DocumentPreviewDialog({
  open,
  doc,
  positions,
  onClose,
  zIndexClass,
  showCreateActions = false,
  createDisabled = false,
  createDisabledReason,
  creating = false,
  onCreate,
  onCreateAndSend,
}: DocumentPreviewDialogProps) {
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestId = useRef(0);
  /** Bleibt über Schließen/Öffnen erhalten, solange der Vorschau-Inhalt gleich ist. */
  const cacheRef = useRef<{ key: string; blob: Blob } | null>(null);

  const previewRequest = useMemo(() => {
    const positionPayload = editorPositionsToPreviewPayload(positions);
    const docPayload = editorDocToPreviewPayload(doc);
    return {
      id: doc.id,
      positionPayload,
      docPayload,
      key: JSON.stringify({ id: doc.id, doc: docPayload, positionen: positionPayload }),
    };
  }, [doc, positions]);
  const previewRequestRef = useRef(previewRequest);
  previewRequestRef.current = previewRequest;
  const previewKey = previewRequest.key;

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      cacheRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      setLoading(false);
      return;
    }

    const cached = cacheRef.current;
    if (cached?.key === previewKey && cached.blob) {
      setPreviewBlob(cached.blob);
      setError(null);
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    setPreviewBlob(null);
    setLoading(true);
    setError(null);

    const { id, positionPayload, docPayload } = previewRequestRef.current;

    void fetchDokumentPreviewPdf(id, positionPayload, controller.signal, docPayload)
      .then((blob) => {
        if (currentRequest !== requestId.current) return;
        const next = asPdfBlob(blob);
        cacheRef.current = { key: previewKey, blob: next };
        setPreviewBlob(next);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (currentRequest !== requestId.current) return;
        setError(err instanceof Error ? err.message : "Vorschau konnte nicht geladen werden.");
      })
      .finally(() => {
        if (currentRequest !== requestId.current) return;
        setLoading(false);
      });

    return () => controller.abort();
    // Nur bei geöffnetem Dialog und inhaltlich geänderter Vorschau neu laden
    // (nicht bei jeder neuen Objekt-Referenz aus dem Editor/Autosave).
  }, [open, previewKey]);

  async function runCreate(action: () => void | Promise<boolean | void>) {
    const result = await action();
    if (result !== false) onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Dokumentvorschau"
      size="4xl"
      fullScreenMobile
      zIndexClass={zIndexClass}
      plainBackdrop
      bodyClassName="flex min-h-0 flex-col"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {showCreateActions ? (
              <>
                <Button
                  type="button"
                  variant="primary"
                  disabled={creating || createDisabled || loading}
                  title={createDisabled ? createDisabledReason : undefined}
                  onClick={() => {
                    if (!onCreate) return;
                    void runCreate(onCreate);
                  }}
                >
                  Erstellen
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={creating || createDisabled || loading}
                  title={
                    createDisabled
                      ? createDisabledReason
                      : "Dokument erstellen und E-Mail-Versand öffnen"
                  }
                  onClick={() => {
                    if (!onCreateAndSend) return;
                    void runCreate(onCreateAndSend);
                  }}
                >
                  Erstellen und Versenden
                </Button>
              </>
            ) : null}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <DokumentPrintAction
              mode="preview"
              docId={doc.id}
              disabled={loading || !previewBlob || Boolean(error) || creating}
              getPreviewBody={() => ({
                positionen: previewRequest.positionPayload,
                doc: previewRequest.docPayload,
              })}
              zIndexClass={zIndexClass}
            />
            <Button type="button" variant="secondary" onClick={onClose} disabled={creating}>
              Schließen
            </Button>
          </div>
        </div>
      }
    >
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div className="relative min-h-[min(72vh,56rem)] overflow-hidden rounded-lg border border-separator bg-fill-quaternary/15">
        {loading && !previewBlob ? (
          <div className="flex min-h-[min(72vh,56rem)] flex-col items-center justify-center gap-2 text-sm text-label-secondary">
            <Spinner />
            <span>Dokumentvorschau wird geladen …</span>
          </div>
        ) : null}
        {previewBlob ? (
          <InlinePdfView
            blob={previewBlob}
            title="Dokumentvorschau"
            className="min-h-[min(72vh,56rem)] w-full"
          />
        ) : null}
      </div>
    </Dialog>
  );
}
